// Consent-first analytics loader
// Only loads GTM/Meta Pixel AFTER cookie consent is given

const CONSENT_KEY = 'cookie_consent';

interface ConsentState {
  status: 'pending' | 'accepted' | 'declined';
  timestamp: string | null;
}

// Check if consent was already given
const hasStoredConsent = (): boolean => {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return false;
    const parsed = JSON.parse(stored) as ConsentState;
    return parsed.status === 'accepted';
  } catch {
    return false;
  }
};

// Track if analytics are loaded
let analyticsLoaded = false;

// Load Google Analytics & GTM
const loadGoogleAnalytics = (): void => {
  // Google tag (gtag.js)
  const gtagScript = document.createElement('script');
  gtagScript.async = true;
  gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=AW-16907474269';
  document.head.appendChild(gtagScript);
  
  gtagScript.onload = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).dataLayer = (window as any).dataLayer || [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function gtag(...args: any[]) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).dataLayer.push(args);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).gtag = gtag;
    gtag('js', new Date());
    gtag('config', 'AW-16907474269');
  };
  
  // Google Tag Manager - slight delay to avoid blocking
  setTimeout(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    const d = document;
    const l = 'dataLayer';
    const i = 'GTM-KK29XRQ3';
    
    w[l] = w[l] || [];
    w[l].push({'gtm.start': new Date().getTime(), event: 'gtm.js'});
    const f = d.getElementsByTagName('script')[0];
    const j = d.createElement('script');
    const dl = l !== 'dataLayer' ? '&l=' + l : '';
    j.async = true;
    j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
    f.parentNode?.insertBefore(j, f);
  }, 100);
};

// Load Meta Pixel
const loadMetaPixel = (): void => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  
  if (w.fbq) return;
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const n: any = function(...args: any[]) {
    if (n.callMethod) {
      n.callMethod.apply(n, args);
    } else {
      n.queue.push(args);
    }
  };
  
  if (!w._fbq) w._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  w.fbq = n;
  
  const t = document.createElement('script');
  t.async = true;
  t.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const s = document.getElementsByTagName('script')[0];
  s.parentNode?.insertBefore(t, s);
  
  w.fbq('init', '1190623529294122');
  w.fbq('track', 'PageView');
};

// Main loader - only loads if consent given
export const loadAnalyticsWithConsent = (): void => {
  if (!hasStoredConsent()) {
    console.log('[Analytics] Waiting for cookie consent...');
    return;
  }
  
  if (analyticsLoaded) return;
  
  console.log('[Analytics] Consent given, loading analytics...');
  analyticsLoaded = true;
  
  // Use requestIdleCallback for better performance
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      loadGoogleAnalytics();
      loadMetaPixel();
    }, { timeout: 3000 });
  } else {
    setTimeout(() => {
      loadGoogleAnalytics();
      loadMetaPixel();
    }, 1000);
  }
};

// Initialize: check on load + listen for consent changes
export const initConsentAnalytics = (): void => {
  // Check if already consented
  if (hasStoredConsent()) {
    loadAnalyticsWithConsent();
  }
  
  // Listen for storage changes (consent given in another tab)
  window.addEventListener('storage', (e) => {
    if (e.key === CONSENT_KEY) {
      loadAnalyticsWithConsent();
    }
  });
  
  // Custom event for when consent is given in the same tab
  window.addEventListener('cookieConsentAccepted', () => {
    loadAnalyticsWithConsent();
  });
};
